import React, { useEffect, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import LocalGroceryStoreOutlinedIcon from "@mui/icons-material/LocalGroceryStoreOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AppRegistrationOutlinedIcon from "@mui/icons-material/AppRegistrationOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import usePersistedState from "../../Hooks/usePersistedState";
import { Link } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AccountMenu from "./AccountMenu";
import CircleNotificationsIcon from "@mui/icons-material/CircleNotifications";
import TemporaryDrawer from "../Drawer/Drawer";
import Badge from "@mui/material/Badge";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import useProtectView from "../../Hooks/useProtectView";
import useSave from "../../Hooks/useSave";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useRef } from "react";
import CakeIcon from "@mui/icons-material/Cake";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import useMediaQuery from "@mui/material/useMediaQuery";
import ReceiptIcon from "@mui/icons-material/Receipt";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("xs")]: {
    width: `calc(${theme.spacing(0)} + 1px)`,
  },
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Container = styled("div")(({ theme }) => ({
  height: theme.mixins.toolbar,
  width: "100%",
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function MiniDrawer(props) {
  const theme = useTheme();
  const [open, setOpen] = usePersistedState("open", false);
  const [openSmall, setOpenSmall] = useState(false);
  const [page, setPage] = useState("");
  const [alerts, setAlerts] = useState([1, 2]);
  const canView = useProtectView();
  const paper = useRef();

  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isBig = useMediaQuery(theme.breakpoints.up("sm"));

  useEffect(() => {
    // console.log(page);
  }, [page]);

  const menu = [
    {
      name: "Dashboard",
      icon: <AssessmentOutlinedIcon fontSize="large" />,
      link: "/",
      active: canView("viewDashboard"),
    },
    {
      name: "Customers",
      icon: <PersonOutlineOutlinedIcon fontSize="large" />,
      link: "/customers",
      active: canView("viewCustomers"),
    },
    {
      name: "Products",
      icon: <LocalGroceryStoreOutlinedIcon fontSize="large" />,
      link: "/products/product-groups",
      active: canView("viewProducts"),
    },
    {
      name: "Orders",
      icon: <AppRegistrationOutlinedIcon fontSize="large" />,
      link: "/orders",
      active: canView("viewOrders"),
    },
    {
      name: "Packing Challah",
      icon: <LunchDiningIcon fontSize="large" />,
      link: "/packing/challah",
      active: canView("viewPacking"),
    },
    {
      name: "Packing Cake",
      icon: <CakeIcon fontSize="large" />,
      link: "/packing/cake",
      active: canView("viewPacking"),
    },
    {
      name: "Reports",
      icon: <SummarizeOutlinedIcon fontSize="large" />,
      link: "/reports/bakers",
      active: canView("viewReports"),
    },
    {
      name: "Settings",
      icon: <SettingsOutlinedIcon fontSize="large" />,
      link: "/settings",
      active: canView("viewSettings"),
    },
    {
      name: "Invoices",
      icon: <ReceiptIcon fontSize="large" />,
      link: "/invoices",
      active: canView("viewInvoices"),
    },
  ];

  const handleDrawerOpen = () => {
    if (isSmall) {
      setOpenSmall(true);
    } else {
      setOpen(true);
    }
  };

  const handleDrawerClose = () => {
    if (isSmall) {
      setOpenSmall(false);
    } else {
      setOpen(false);
    }
  };

  const handleClick = (page) => {
    setPage(page);
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <CssBaseline />
      <AppBar position="fixed" open={isSmall ? openSmall : open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: "36px",
              ...(isSmall ? openSmall : open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" key="6">
            {page}
          </Typography>

          <IconButton
            sx={(theme) => ({
              display: "none",
              [theme.breakpoints.down("md")]: {
                display: "block",
                marginLeft: "auto",
                color: "white",
              },
            })}
            onClick={() => window.location.reload()}
          >
            <RefreshIcon />
          </IconButton>

          <TemporaryDrawer>
            <Badge
              badgeContent={alerts.length}
              overlap="circular"
              color="secondary"
            >
              <CircleNotificationsIcon
                fontSize="large"
                sx={{
                  color: "gray",
                  backgroundColor: "white",
                  borderRadius: "50px",
                }}
              />
            </Badge>
            <Box>
              {alerts.map((item, index) => (
                <Card
                  sx={{
                    minWidth: 275,
                    margin: "20px",
                    // bgcolor: "primary.light",
                  }}
                >
                  <CardContent>
                    <Typography
                      sx={{ fontSize: 14 }}
                      color="white"
                      gutterBottom
                    >
                      {item}
                    </Typography>
                    <Typography sx={{ mb: 1.5 }}>adjective</Typography>
                    <Typography variant="body2">
                      well meaning and kindly.
                      <br />
                      {'"a benevolent smile"'}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      onClick={() =>
                        setAlerts((alt) => [...alt, alt.length + 1])
                      }
                    >
                      actions
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setAlerts((state) => {
                          const copyState = [...state];

                          copyState.splice(index, 1);

                          return copyState;
                        });
                      }}
                    >
                      Approve
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </Box>
          </TemporaryDrawer>

          <AccountMenu>
            {/* <Typography sx={{ mr: "5px", color: "white" }}>
              {`Hello,\u00A0 ${user.name}`}
            </Typography> */}
            <AccountCircleIcon
              fontSize="large"
              sx={{ backgroundColor: "white", borderRadius: "50px" }}
            />
          </AccountMenu>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={isSmall ? openSmall : open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        {menu.map((item, index) =>
          item.active ? (
            <Link
              to={item.link}
              style={{
                textDecoration: "none",
                color: "rgb(33,33,33,1)",
                margin: "0px",
              }}
              key={index}
            >
              <ListItem divider={true} onClick={() => handleClick(item.name)}>
                <ListItemIcon>{item.icon}</ListItemIcon>

                <ListItemText>{item.name}</ListItemText>
              </ListItem>
            </Link>
          ) : null
        )}
      </Drawer>

      {
        <Box
          ref={paper}
          sx={{
            padding: "20px",
            marginTop: "62px",
            width: "100%",
            overflow: "auto",
            // height: "calc(100vh - 62px)",
          }}
        >
          {props.children(paper)}
        </Box>
      }
    </Box>
  );
}
